import { AGENT_ID, EXTERNAL_AGENT_PRESENCE_KIND } from '@canvas/protocol-types';

import { type ClientId } from './telepointers.ts';
import rovoAvatarUrl from './ui/rovo-agent-avatar.svg';

export { AGENT_ID, EXTERNAL_AGENT_PRESENCE_KIND };

export type AccountId = string;

/**
 * The full Identity user payload API endpoints such as /gateway/api/me
 */
export interface FullIdentityApiUserPayload {
  account_id: AccountId;
  nickname: string;
  picture: string;
  email: string;
  account_type: string;
  name: string;
  account_status: string;
  zoneinfo: string;
  locale: string;
  extended_profile: Record<string, any>;
  email_verified: boolean;
}

/**
 * To enable tagged unions for Identity types and differentiating between them at runtime
 */
export const IdentityKind = {
  CanvasId: 'canvas',
  AccountIdOnly: 'identity-account-id-only',
  AnonymousId: 'anonymous',
  AiAgent: 'ai-agent',
} as const;

/**
 * Denotes a 'partially' loaded identity where only the accountId is known, with all other fields undefined
 */
export type IdentityAccountIdOnly = {
  accountId: AccountId;
  displayName: undefined;
  nickname: undefined;
  picture: undefined;
  email: undefined;
  locale: undefined;
  type: typeof IdentityKind.AccountIdOnly;
};

/**
 * The set of Identity fields that Canvas depends on
 * @privateRemark We need to explicitly make a new type and still support the old type
 * kicking around in the codebase since it could’ve possibly been stored in customers' indexedDBs.
 * It made sense to extend the original interface with a new field here since that’s the only
 * meaningful difference
 */
export interface CanvasIdentity {
  type: typeof IdentityKind.CanvasId;
  accountId: AccountId;
  /**
   * User's name, applies relevant privacy restrictions
   */
  displayName: string;
  /**
   * @deprecated use `displayName` instead as it shows the correct name depending on the user's configured privacy restrictions
   */
  nickname: string;
  picture: string;
  email?: string;
  locale: string; // eg en-US, the full locale from identity
}

/**
 * @see {@link https://developer.atlassian.com/platform/identity/learning/edge-authenticator/uct/#authentication-type-/-api-specific-claims}
 */
export const ANONYMOUS_ID = 'unidentified' as const;
export const AGENT_CLIENT_ID = 0xa1 as ClientId;
export const ROVO_AGENT_PRESENCE_KIND = 'rovo-agent' as const;
export const BACKEND_ROVO_AGENT_PRESENCE_KIND = 'backend-rovo-agent' as const;

/**
 * Agent types that NCS publishes as Rovo rather than as an external agent. Keep in
 * sync with `INTERNAL_AGENT_TYPES` in NCS:
 * https://bitbucket.org/atlassian/pf-collab-service/src/ca0f57065e9dd291bbc9630acf6162851e1dd028/src/koa/controllers/agents/canvas-awareness.ts#lines-21
 */
const ROVO_AGENT_TYPES: ReadonlySet<string> = new Set(['rovo_chat', 'convo-ai']);

export const isRovoAgentType = (agentType: unknown): agentType is string =>
  typeof agentType === 'string' && ROVO_AGENT_TYPES.has(agentType);

export interface Anonymous {
  accountId: typeof ANONYMOUS_ID;
  displayName: undefined;
  nickname: undefined;
  picture: undefined;
  email: undefined;
  locale: undefined;
  type: typeof IdentityKind.AnonymousId;
}

interface BaseAiAgentIdentity {
  accountId: AccountId;
  displayName: string | undefined;
  nickname: undefined;
  picture: string;
  email: undefined;
  locale: undefined;
  agentId?: string;
  agentType?: string;
  /**
   * Local profile lookup state; never published in awareness.
   */
  agentProfileStatus?: 'loading' | 'settled';
  type: typeof IdentityKind.AiAgent;
}

export interface RovoAgentIdentity extends BaseAiAgentIdentity {
  presenceKind: typeof ROVO_AGENT_PRESENCE_KIND;
}

/**
 * Rovo presence that NCS or canvas-server publishes for a user. It has no camera,
 * so the frontend Rovo participant still drives camera and follow behaviour.
 * `agentOwnerAccountId` is absent when NCS does not send the acting user.
 */
export interface BackendRovoAgentIdentity extends BaseAiAgentIdentity {
  presenceKind: typeof BACKEND_ROVO_AGENT_PRESENCE_KIND;
  agentType: string;
  agentOwnerAccountId: string | undefined;
  agentOwnerName?: string;
}

export interface ExternalAgentIdentity extends BaseAiAgentIdentity {
  presenceKind: typeof EXTERNAL_AGENT_PRESENCE_KIND;
  agentOwnerAccountId: string;
  agentOwnerName?: string;
}

export type AiAgentIdentity =
  | RovoAgentIdentity
  | BackendRovoAgentIdentity
  | ExternalAgentIdentity;

export const ANONYMOUS_USER: Anonymous = {
  accountId: ANONYMOUS_ID,
  displayName: undefined,
  nickname: undefined,
  picture: undefined,
  email: undefined,
  locale: undefined,
  type: IdentityKind.AnonymousId,
};

export const AI_AGENT_USER: RovoAgentIdentity = {
  accountId: AGENT_ID,
  displayName: undefined,
  nickname: undefined,
  picture: rovoAvatarUrl,
  email: undefined,
  locale: undefined,
  presenceKind: ROVO_AGENT_PRESENCE_KIND,
  type: IdentityKind.AiAgent,
};
/**
 * Represents the current user's identity state, which can either be
 * - authenticated
 * - anonymous
 * Differs from Identity as there's no partial state for your own identity (it's all or nothing)
 */
export type UserIdentity = CanvasIdentity | Anonymous | AiAgentIdentity;

/**
 * Unified identity type to encode the different types of identity we accept, including a partial state
 * if we can't fetch the full identity of an accountId
 */
export type Identity = UserIdentity | IdentityAccountIdOnly;
